import { Component, ElementRef, ViewChild, AfterViewInit, OnDestroy, inject, OnInit } from '@angular/core';
import { fromEvent, Subject } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';
import { SidebarComponent } from '../../../core/components/sidebar/sidebar/sidebar.component';
import { EditorComponent } from '../../../core/components/editor/editor.component';
import { DiagramCanvasComponent } from '../../../core/components/diagram-canvas/component/diagram-canvas.component';
import { ToolbarComponent } from '../../../core/components/toolbar/components/toolbar.component';
import { DiagramApiService } from '../../../core/services/diagram/diagram.api.service';
import { EditorService } from '../../../core/services/editor/editor.service';
import { ToastrService } from 'ngx-toastr';
import { ExportPngService } from '../../../core/services/export-png/export-png.service';
import { ToolbarAction } from '../types/toolbarAction.types';
import { ResizeElements } from '../types/resizeElements.type';
import { SavedSequenceDiagram } from '../../../core/model/diagram/sequenceDiagram';
import { ActivatedRoute, Router } from '@angular/router';
import { MAX_EDITOR_WIDTH_PX, MIN_EDITOR_WIDTH_PX, PRINT_BACKGROUND_COLOR } from '../constants/sequence-diagram-page.constants';
import { EXPORT_PNG_ICON_SVG, HELP_ICON_SVG, SAVE_ICON_SVG } from '../../../core/components/toolbar/svg/svg-toolbar.constant';
import { DiagramHelpModalComponent } from '../../../core/components/diagram-help-modal/diagram-help-modal.component';

@Component({
  selector: 'app-sequence-diagram-page',
  standalone: true,
  imports: [SidebarComponent, EditorComponent, DiagramCanvasComponent, ToolbarComponent, DiagramHelpModalComponent],
  templateUrl: './sequence-diagram-page.component.html',
  styleUrls: ['./sequence-diagram-page.component.scss'],
})
export class SequenceDiagramPageComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('editor', { read: ElementRef }) editorRef!: ElementRef<HTMLElement>;
  @ViewChild('canvas', { read: ElementRef }) canvasRef!: ElementRef<HTMLElement>;
  @ViewChild('container', { read: ElementRef }) containerRef!: ElementRef<HTMLElement>;
  @ViewChild('resizer', { read: ElementRef }) resizerRef!: ElementRef<HTMLElement>;

  private _destroy$ = new Subject<void>();
  private _resizeElements!: ResizeElements;

  private readonly _diagramApiService = inject(DiagramApiService);
  private readonly _editorService = inject(EditorService);
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _toastr = inject(ToastrService);

  diagramId: number | null = null;
  private readonly _exportPngService = inject(ExportPngService);

  activeTab: 'editor' | 'canvas' = 'editor';
  showHelpModal = false;

  toolbarActions: ToolbarAction[] = [
    {
      label: 'Enregistrer',
      ariaLabel: 'Sauvegarder le diagramme',
      svg: SAVE_ICON_SVG,
      action: () => this.handleSave(),
    },
    {
      label: 'Exporter en PNG',
      ariaLabel: 'Exporter le diagramme en PNG',
      svg: EXPORT_PNG_ICON_SVG,
      action: () => this.handleExportPng(),
    },
    {
      label: 'Aide',
      ariaLabel: "Afficher l'aide sur la syntaxe",
      svg: HELP_ICON_SVG,
      action: () => this.handleHelp(),
    },
  ];

  ngOnInit(): void {
    this._route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.diagramId = parseInt(id, 10);
        this.loadDiagram(this.diagramId);
      }
    });
  }

  loadDiagram(id: number): void {
    this._diagramApiService.getDiagramById(id).subscribe({
      next: diagram => {
        if (diagram) {
          this._editorService.setDiagramText(diagram.sequenceDiagramContent);
        } else {
          this._toastr.error('Diagramme non trouvé.');
          this._router.navigate(['/sequence-diagram']);
        }
      },
      error: () => {
        this._toastr.error('Erreur lors du chargement du diagramme.');
      },
    });
  }

  setActiveTab(tab: 'editor' | 'canvas'): void {
    this.activeTab = tab;
  }

  ngAfterViewInit(): void {
    this._initializeResizer();
  }

  private _initializeResizer(): void {
    if (!this.resizerRef) return;

    this._resizeElements = {
      container: this.containerRef.nativeElement,
      editor: this.editorRef.nativeElement,
      canvas: this.canvasRef.nativeElement,
    };

    const resizer = this.resizerRef.nativeElement;
    const mousedown$ = fromEvent<MouseEvent>(resizer, 'mousedown');
    const mousemove$ = fromEvent<MouseEvent>(document, 'mousemove');
    const mouseup$ = fromEvent<MouseEvent>(document, 'mouseup');

    mousedown$
      .pipe(
        switchMap(startEvent => {
          startEvent.preventDefault();
          return mousemove$.pipe(
            takeUntil(mouseup$),
            tap(moveEvent => this._resizeContainer(moveEvent))
          );
        }),
        takeUntil(this._destroy$)
      )
      .subscribe();
  }

  private _resizeContainer(event: MouseEvent): void {
    const container = this._resizeElements.container;
    const editor = this._resizeElements.editor;
    const containerRect = container.getBoundingClientRect();
    const resizerWidth = this.resizerRef.nativeElement.offsetWidth;
    const mouseX = event.clientX - containerRect.left;
    const maxWidth = containerRect.width - resizerWidth - MIN_EDITOR_WIDTH_PX;
    const clampedWidth = Math.max(MIN_EDITOR_WIDTH_PX, Math.min(mouseX, maxWidth, MAX_EDITOR_WIDTH_PX));
    const flexPercent = (clampedWidth / containerRect.width) * 100;

    editor.style.flexBasis = `${flexPercent}%`;
  }

  private _getCanvasElement(): HTMLCanvasElement | null {
    return this.canvasRef.nativeElement.querySelector('canvas');
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  handleSave(): void {
    const text = this._editorService.getCurrentDiagramText();
    const title = this._editorService.getCurrentDiagramTitle() || 'Sans titre';

    if (this.diagramId) {
      this._updateDiagram(this.diagramId, text, title);
      return;
    }

    this._createDiagram(text, title);
  }

  private _updateDiagram(id: number, text: string, title: string): void {
    this._diagramApiService.updateDiagram(id, text, title).subscribe({
      next: () => this._toastr.success('Diagramme mis à jour avec succès.'),
      error: () => this._toastr.error('Erreur lors de la mise à jour du diagramme.'),
    });
  }

  private _createDiagram(text: string, title: string): void {
    this._diagramApiService.postDiagram(text, title).subscribe({
      next: (response: SavedSequenceDiagram) => {
        this.diagramId = response.id;
        this._toastr.success('Diagramme sauvegardé avec succès.');
      },
      error: () => this._toastr.error('Veuillez créez un compte pour sauvegarder votre diagramme.'),
    });
  }

  handleExportPng(): void {
    const canvas = this._getCanvasElement();

    if (!canvas) {
      this._toastr.error(`Impossible d'exporter le diagramme.`);
      return;
    }

    try {
      const printCanvas = this._createPrintVersion(canvas);

      this._exportPngService.exportCanvas(printCanvas, {
        filename: this._editorService.getCurrentDiagramTitle(),
      });

      this._toastr.success('Diagramme exporté avec succès.');
    } catch {
      this._toastr.error(`Erreur lors de l'export-png du diagramme.`);
    }
  }

  handleHelp(): void {
    this.showHelpModal = true;
  }

  closeHelpModal(): void {
    this.showHelpModal = false;
  }

  private _createPrintVersion(originalCanvas: HTMLCanvasElement): HTMLCanvasElement {
    const printCanvas = document.createElement('canvas');
    const ctx = printCanvas.getContext('2d')!;

    printCanvas.width = originalCanvas.width;
    printCanvas.height = originalCanvas.height;

    ctx.fillStyle = PRINT_BACKGROUND_COLOR;
    ctx.fillRect(0, 0, printCanvas.width, printCanvas.height);

    ctx.drawImage(originalCanvas, 0, 0);

    return printCanvas;
  }
}
